import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Banner } from "../ui/Banner.js";
import { Card } from "../ui/Card.js";
import { KpiWartosc, type Trend } from "../ui/KpiTile.js";
import { Tooltip } from "../ui/Tooltip.js";
import { StanLadowania, StanPusty } from "../ui/stany.js";
import type { LocalEstimate } from "../../pages/MicPage.js";
import type { MicHistoryEntry, MicShotResponse } from "../../lib/api.js";

interface Props {
  shot: MicShotResponse | null;
  localEstimate: LocalEstimate;
  pending: boolean;
  /** Poprzednia proba z historii. Zrodlo trendu w kafelkach (KP-1). */
  poprzednia: MicHistoryEntry | null;
  /**
   * Puszcza to samo nagranie przez wszystkich dostawcow. undefined, gdy nie ma
   * czego porownac - proba niezapisana albo bez nagrania na dysku.
   */
  naPorownaj?: () => void;
  porownanieTrwa?: boolean;
}

/** Opisy flag jakosci po polsku. Uzytkownik nie ma czytac stalych z kodu. */
const FLAGI: Record<string, string> = {
  TOO_SHORT: "nagranie za krótkie",
  TOO_LONG: "nagranie za długie, żeby zmierzyć sposób mówienia",
  TOO_QUIET: "nagranie za ciche",
  CLIPPING: "sygnał przesterowany",
  NO_VOICED: "brak mowy w nagraniu",
  LOW_VOICED_RATIO: "za mało mowy, żeby ocenić ton",
  BAND_LIMITED: "obcięte niskie częstotliwości",
  TOO_FEW_SYLLABLES: "za krótka wypowiedź, żeby zmierzyć tempo",
};

/** KP-2: format polski, czyli przecinek dziesietny. */
function liczba(wartosc: number, cyfry = 1): string {
  return wartosc.toFixed(cyfry).replace(".", ",");
}

/**
 * Znacznik przy wyniku: DOSTAWCA i wersja, nie sama wersja.
 *
 * Wybor w ustawieniach stoi na poziomie dostawcy, wiec to jego nazwa musi dac
 * sie zestawic z panelem jednym spojrzeniem. Sama wersja tego nie zalatwia -
 * zeby z "gemini-3.5-flash" wyczytac, ze zamiast Qwena odpowiedzial ktos inny,
 * trzeba znac katalog wersji na pamiec. Zgloszenie z 17.08.2026 bylo dokladnie
 * o to: "zaznaczam jedno, a przy mikrofonie widze inny model".
 */
function znacznik(dostawca: string | null, model: string): string {
  return dostawca ? `${dostawca} · ${model}` : model;
}

/**
 * Zdanie o tym, ze dostawca policzyl INNA WERSJA MODELU, niz zamowiliśmy.
 *
 * Pojawia sie wylacznie przy realnej rozbieznosci - gdy dostawca odeslal nazwe
 * modelu i jest ona inna niz nasza. Do 19.08.2026 taka podmiana nie byla nigdzie
 * widoczna: dziennik wywolan dostawal wersje z odpowiedzi, a ekran zamowiona,
 * przez co ta sama ocena mogla mieć dwie rozne nazwy modelu w dwoch miejscach.
 */
function podmianaWersji(zamowiona: string, uzyta: string | null): string {
  return `Zamówiliśmy wersję „${zamowiona}", a dostawca policzył wersją „${uzyta ?? "nieznaną"}". To jego podmiana, nie nasza - wynik pochodzi z tej drugiej.`;
}

/** Zdanie o podmianie dostawcy. Pojawia sie tylko wtedy, gdy do niej doszlo. */
function podmiana(zamowiony: string, uzyty: string | null): string {
  return `W ustawieniach wybrano „${zamowiony}", ale ten dostawca nie jest skonfigurowany na tym serwerze - wynik policzył „${uzyty ?? "inny"}". Sprawdź klucze w Ustawieniach.`;
}

/**
 * Trend wzgledem poprzedniej proby (KP-1).
 *
 * Pomiar bezwzgledny mowi malo - dopiero porownanie dwoch podejsc do tego samego
 * zdania pokazuje, czy zmiana sposobu mowienia cokolwiek dala. Roznica ponizej
 * progu zaokraglenia nie jest trendem, tylko szumem, wiec nie pokazujemy jej wcale.
 */
function trend(teraz: number | null | undefined, przed: number | null | undefined, jednostka: string): Trend | undefined {
  if (teraz === null || teraz === undefined || przed === null || przed === undefined) return undefined;
  const roznica = teraz - przed;
  if (Math.abs(roznica) < 0.05) return undefined;
  return {
    etykieta: `${roznica > 0 ? "+" : "-"}${liczba(Math.abs(roznica))} ${jednostka}`,
    kierunek: roznica > 0 ? "wzrost" : "spadek",
  };
}

/**
 * Wynik strzalu z mikrofonu w DWOCH kartach-siostrach, nie w rzedzie czterech
 * kafelkow.
 *
 * Powod podzialu: "Tempo" i "Ton" to podrecznikowe KPI (jedna krotka liczba),
 * a "Transkrypcja" trzyma cale zdanie i "Emocja" trzy poziomy tekstu - w jednym
 * rzedzie KP-3 wymuszalby wysokosc najdluzszego kafelka. Podzial ma tez skutek
 * znaczeniowy: rozroznienie POMIARU od INTERPRETACJI niesie sam ksztalt
 * interfejsu, nie tylko znacznik przy kafelku (A-7).
 *
 * Obie karty renderuja sie ZAWSZE, takze przed pierwszym nagraniem (S-5, L-8):
 * uklad ma nie skakac, gdy pojawia sie wynik.
 */
export function MicWynik({
  shot,
  localEstimate,
  pending,
  poprzednia,
  naPorownaj,
  porownanieTrwa = false,
}: Props): React.JSX.Element {
  const prosody = shot?.prosody;

  /*
   * Ton pokazujemy z podgladu, zanim serwer odpowie - ale to jest WARTOSC
   * WSTEPNA, nie ta sama liczba.
   *
   * Ten sam kod (shared/prosodyCore.ts), inne warunki: podglad liczy YIN na
   * ramkach 2048 probek przy czestotliwosci karty dzwiekowej (zwykle 48 kHz),
   * co trzecia klatka animacji i w wezszym zakresie 70-400 Hz; serwer liczy na
   * ramkach 40 ms przy 16 kHz, z krokiem 20 ms, w zakresie 60-450 Hz i z
   * bramkami jakosci. Wyniki sa BLISKIE, nie identyczne, i podpis pod kafelkiem
   * mowi o tym wprost.
   *
   * Dlatego TREND liczymy wylacznie z pomiaru serwerowego - porownanie liczby
   * z podgladu z historia zestawialoby dwie rozne metody i pokazywaloby zmiane
   * tam, gdzie zmienil sie tylko sposob liczenia.
   */
  const zPodgladu = !prosody;
  const tempo = prosody?.syllablesPerSecond ?? null;
  const rozpietosc = prosody ? prosody.f0RangeSemitones : localEstimate.f0RangeSemitones;
  const wysokosc = prosody ? prosody.f0MedianHz : localEstimate.f0MedianHz;

  const flagi = (prosody?.flags ?? []).map((f) => FLAGI[f] ?? f);

  /**
   * Stan pusty nalezy sie TYLKO wtedy, gdy naprawde nie ma czego pokazac.
   *
   * `pending` jest prawdziwe dopiero po zatrzymaniu nagrania, wiec w trakcie
   * mowienia karta pisala "Jeszcze nie ma pomiaru" rownoczesnie z rysowaniem
   * konturu tonu obok - dwa przeciwstawne komunikaty na jednym ekranie. Podglad
   * na zywo liczy sie jako wynik wstepny, wiec gdy ma juz jakakolwiek liczbe,
   * pokazujemy kafelki zamiast stanu pustego.
   */
  const maPodglad = localEstimate.f0RangeSemitones !== null || localEstimate.f0MedianHz !== null;
  const bezWyniku = !shot && !pending && !maPodglad;

  /**
   * Trwa nagrywanie albo analiza - wyniku z serwera jeszcze nie ma.
   *
   * Kafelek "Tempo" MUSI byc wtedy w stanie ladowania, a nie w stanie braku.
   * Tempa nie da sie policzyc z podgladu, bo podglad sledzi tylko ton; bez tego
   * rozroznienia kafelek pokazywalby dymek "wypowiedz byla za krotka" w chwili,
   * gdy uzytkownik wciaz mowi. To dokladnie ten rodzaj zgadywania, ktorego ta
   * aplikacja ma nie robic.
   */
  const wTrakcie = !shot && (pending || maPodglad);

  /**
   * Czemu nie ma liczby w karcie wymiarow. TRZY rozne powody, trzy rozne zdania.
   *
   * Bez tego rozroznienia kreska na ekranie znaczylaby naraz "warstwa
   * wylaczona", "model wstaje" i "sidecar padl" - a to sa trzy rozne rzeczy i
   * kazda ma inna reakcje uzytkownika.
   */
  const powodBrakuWymiarow =
    shot?.serStan === "zaDlugie"
      ? `${shot.serError ?? "Nagranie jest dłuższe niż okno tego modelu."} Pomiar tempa i tonu obok liczy się z całego nagrania i pozostaje ważny.`
      : shot?.serStan === "wczytywanie"
        ? "Model wymiarów nie zdążył wstać w czasie, który mu daliśmy. To nie jest awaria - nagraj ponownie."
        : shot?.serError
          ? "Model wymiarów nie odpowiedział. To nie znaczy, że w nagraniu nic nie było - pomiar obok pozostaje ważny."
          : shot?.serStan === null || shot?.serStan === undefined
            ? "Warstwa sygnałowa jest wyłączona w Ustawieniach."
            : "Nagranie było za krótkie, żeby model wymiarowy cokolwiek usłyszał.";

  return (
    <>
      <Card tytul="Pomiar">
        {/* BN-1/BN-2: baner trwajacego stanu - jest tak dlugo, jak dlugo sygnal
            ma wady, i znika sam, gdy nastepne nagranie wyjdzie czysto. */}
        {flagi.length > 0 && (
          <Banner
            wariant="warning"
            tekst={`Jakość nagrania: ${flagi.join(", ")}. Tam, gdzie widzisz kreskę, wolimy nie podać liczby, niż podać zgadniętą.`}
          />
        )}

        {bezWyniku ? (
          <StanPusty
            tytul="Jeszcze nie ma pomiaru"
            opis="Nagraj jedno zdanie, a tempo i ton policzymy z samego sygnału."
          />
        ) : (
          <div className="mic-metryki">
            <div>
              <KpiWartosc
                etykieta="Tempo"
                wartosc={tempo === null ? null : liczba(tempo)}
                jednostka="sylab na sekundę"
                druga={shot?.paceLabel ?? undefined}
                trend={trend(tempo, poprzednia?.syllablesPerSecond, "syl./s")}
                powodBraku="Wypowiedź była za krótka, żeby zmierzyć tempo. Nie pokazujemy liczby, której nie mierzymy."
                ladowanie={wTrakcie}
              />
              {shot?.paceHint && <p className="mic-metryka__podpowiedz">{shot.paceHint}</p>}
            </div>

            <div>
              <KpiWartosc
                etykieta="Ton"
                wartosc={rozpietosc === null ? null : liczba(rozpietosc)}
                jednostka="półtonu rozpiętości"
                druga={wysokosc === null ? undefined : `wysokość ${Math.round(wysokosc)} Hz`}
                trend={trend(prosody?.f0RangeSemitones, poprzednia?.f0RangeSemitones, "półtonu")}
                powodBraku="W nagraniu było za mało dźwięcznej mowy, żeby zmierzyć rozpiętość tonu."
                ladowanie={wTrakcie && rozpietosc === null}
              />
              {zPodgladu && rozpietosc !== null ? (
                <p className="mic-metryka__podpowiedz">
                  Wartość wstępna z podglądu na żywo. Po analizie zastąpi ją pomiar z serwera.
                </p>
              ) : (
                shot?.toneHint && <p className="mic-metryka__podpowiedz">{shot.toneHint}</p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/*
        TRZECIA KARTA STOI MIEDZY POMIAREM A OCENA MODELU I TO JEST DECYZJA.
        Czytana z gory na dol kolejnosc kart to rosnaca odleglosc od surowego
        dzwieku: sygnal -> wymiary z sygnalu -> interpretacja tresci. Wrzucenie
        tych liczb do karty "Pomiar" zlaloby model z pomiarem, czyli dokladnie
        ten blad, przed ktorym broni komentarz przy tym komponencie.
      */}
      <Card tytul="Wymiary z sygnału">
        {bezWyniku ? (
          <StanPusty
            tytul="Jeszcze nie ma wymiarów"
            opis="Osobny model słucha samego brzmienia i ocenia trzy wymiary. Nie zna słów."
          />
        ) : (
          <>
            <div className="ocena-model__head">
              <p className="mic-metryka__podpowiedz" style={{ margin: 0 }}>
                Model słucha samego brzmienia. Nie dostaje transkrypcji i nie zna słów, więc nie ma
                jak pójść za treścią.
              </p>
              {shot?.serModel && (
                <Tooltip tekst={`Wymiary policzył model sygnałowy, wagi ${shot.serModel}.`}>
                  <Badge>{shot.serModel}</Badge>
                </Tooltip>
              )}
            </div>

            {/*
              KAZDY KAFELEK W SWOIM <div>, I TO NIE JEST OZDOBA.

              KpiWartosc zwraca FRAGMENT - etykiete, slot trendu i wiersz
              wartosci jako trzy osobne wezly. Wstawiony wprost do siatki daje
              wiec nie jeden kafelek, tylko trzy komorki, a trzy kafelki
              rozsypuja sie na dziewiec komorek: etykieta "Pobudzenie" ladowala
              w kolumnie pierwszej, jej kreska w trzeciej, a "Dominacja" w
              nastepnym wierszu pod cudza wartoscia. Karta "Pomiar" wyzej ma te
              opakowania od poczatku i dlatego wygladala poprawnie - ta ich nie
              miala. Zgloszenie z 23.08.2026: "nie widze tutaj zadnego wyniku".
            */}
            <div className="mic-metryki">
              <div>
                <KpiWartosc
                  etykieta="Pobudzenie"
                  wartosc={shot?.serArousal === null || shot?.serArousal === undefined ? null : liczba(shot.serArousal, 2)}
                  jednostka="ile energii słychać w głosie"
                  trend={trend(shot?.serArousal, poprzednia?.serArousal, "")}
                  powodBraku={powodBrakuWymiarow}
                  ladowanie={wTrakcie}
                />
              </div>
              <div>
                <KpiWartosc
                  etykieta="Wydźwięk"
                  wartosc={shot?.serValence === null || shot?.serValence === undefined ? null : liczba(shot.serValence, 2)}
                  jednostka="od chłodnego do ciepłego"
                  trend={trend(shot?.serValence, poprzednia?.serValence, "")}
                  powodBraku={powodBrakuWymiarow}
                  ladowanie={wTrakcie}
                />
              </div>
              <div>
                <KpiWartosc
                  etykieta="Dominacja"
                  wartosc={shot?.serDominance === null || shot?.serDominance === undefined ? null : liczba(shot.serDominance, 2)}
                  jednostka="ile pewności i kontroli słychać"
                  trend={trend(shot?.serDominance, poprzednia?.serDominance, "")}
                  powodBraku={powodBrakuWymiarow}
                  ladowanie={wTrakcie}
                />
              </div>
            </div>

            {/* Kotwica zamiast etykiet pasm: progi mieszkaja w methodology/, nie
                w kliencie, a bez nich "raczej cieply" bylby zgadywaniem. */}
            <p className="mic-metryka__podpowiedz">
              Pobudzenie i dominacja w skali od 0 do 1. Wydźwięk od -1 do +1: -1 chłodno, 0
              neutralnie, +1 ciepło.
            </p>

            {/* NAGRANIE ZA DLUGIE STOI PRZED AWARIA, bo tylko ono ma lekarstwo
                po stronie uzytkownika: powiedziec krocej. Model ma wlasne okno,
                krotsze niz limit nagrywania z panelu. */}
            {shot?.serStan === "zaDlugie" && (
              <p className="ocena-model__detal">
                {shot.serError} Tempo, ton i ocena modelu obok policzyły się z całego nagrania -
                tylko wymiary z sygnału potrzebują krótszego fragmentu.
              </p>
            )}

            {/* Wczytywanie to NIE awaria i nie wolno go tak pokazac. Sidecar
                trzyma jeden model naraz i wypycha go z pamieci po kwadransie
                bezczynnosci. Serwer czeka teraz na to wczytanie sam, wiec ten
                komunikat znaczy juz cos WEZSZEGO niz kiedys: nie doczekal. */}
            {shot?.serStan === "wczytywanie" && (
              <p className="ocena-model__detal">
                {shot.serSzacowanyCzasS !== null && shot.serSzacowanyCzasS > 60
                  ? `Wagi tego modelu nie leżą jeszcze na dysku serwera - pobranie zajmuje około ${shot.serSzacowanyCzasS} s i idzie w tle.`
                  : "Model wymiarów nie zdążył wstać w czasie, który mu daliśmy."}{" "}
                Pozostałe wyniki są kompletne - nagraj ponownie, a wymiary też się pojawią.
              </p>
            )}
            {shot?.serError && shot.serStan !== "zaDlugie" && (
              <p className="ocena-model__detal">
                Warstwa sygnałowa jest niedostępna, pomiar i ocena modelu obok pozostają ważne.
                Szczegół techniczny: {shot.serError}
              </p>
            )}

            <p className="mic-stopka">
              Te trzy liczby liczy model, nie pomiar. Modele tego typu uczy się głównie na
              nagraniach angielskich, a po polsku nie zostały u nas jeszcze zmierzone. Najlepiej
              przenosi się między językami pobudzenie, najsłabiej wydźwięk - dlatego traktuj
              wydźwięk jako wskazówkę, nie jako werdykt.
            </p>
          </>
        )}
      </Card>

      <Card tytul="Ocena modelu">
        {bezWyniku ? (
          <StanPusty
            tytul="Jeszcze nie ma oceny"
            opis="Po nagraniu model rozpozna treść wypowiedzi i opisze, jak zabrzmiała."
          />
        ) : !shot ? (
          <StanLadowania tekst="Analizuję nagranie..." />
        ) : (
          <>
            <div className="ocena-model">
              <div className="ocena-model__head">
                <h3 className="ocena-model__tytul">Transkrypcja</h3>
                {shot.transcriptModel && (
                  <Tooltip
                    tekst={`Treść rozpoznał dostawca ${shot.transcriptProvider ?? "?"}, wersja ${shot.transcriptModel}.`}
                  >
                    <Badge>{znacznik(shot.transcriptProvider, shot.transcriptModel)}</Badge>
                  </Tooltip>
                )}
              </div>
              <p className="ocena-model__tresc">{shot.text || "Nie usłyszałem mowy."}</p>
              {shot.transcriptFallbackFrom && (
                <p className="ocena-model__detal">
                  {podmiana(shot.transcriptFallbackFrom, shot.transcriptProvider)}
                </p>
              )}
              {shot.transcriptModelRequested && (
                <p className="ocena-model__detal">
                  {podmianaWersji(shot.transcriptModelRequested, shot.transcriptModel)}
                </p>
              )}
            </div>

            <div className="ocena-model">
              <div className="ocena-model__head">
                <h3 className="ocena-model__tytul">Emocja</h3>
                {shot.emotionModel && (
                  <Tooltip
                    tekst={`Brzmienie ocenił dostawca ${shot.emotionProvider ?? "?"}, wersja ${shot.emotionModel}.`}
                  >
                    <Badge>{znacznik(shot.emotionProvider, shot.emotionModel)}</Badge>
                  </Tooltip>
                )}
              </div>
              {shot.emotionFallbackFrom && (
                <p className="ocena-model__detal">
                  {podmiana(shot.emotionFallbackFrom, shot.emotionProvider)}
                </p>
              )}
              {shot.emotionModelRequested && (
                <p className="ocena-model__detal">
                  {podmianaWersji(shot.emotionModelRequested, shot.emotionModel)}
                </p>
              )}
              {/* Ponowienie u tego samego dostawcy nie jest awarią, ale było
                  płatne i użytkownik ma prawo o nim wiedzieć - inaczej rachunek
                  nie zgadza się z tym, co widać na ekranie. */}
              {shot.emotionProby !== null && shot.emotionProby > 1 && (
                <p className="ocena-model__detal">
                  Dostawca odbił pierwsze wywołanie - wynik pochodzi z próby numer {shot.emotionProby}.
                </p>
              )}
              {/* Awaria modelu NIE jest tym samym co brak sygnału w nagraniu.
                  Jeden stan da się naprawić ustawieniami, drugi tylko innym
                  nagraniem - mylący je komunikat kosztował dni szukania. */}
              <p className="ocena-model__tresc">
                {shot.emotionError ? "Model nie odpowiedział." : (shot.emotionLabel ?? "Nie da się określić.")}
              </p>
              {shot.emotionError ? (
                <p className="ocena-model__detal">
                  Ocena brzmienia jest niedostępna, pomiar tempa i tonu obok pozostaje ważny. Szczegół
                  techniczny: {shot.emotionError}
                </p>
              ) : shot.vocalCuesConflict ? (
                /* Opis modelu przeczył pomiarowi, więc nie stoi tu jako wynik - ale
                   jest zacytowany w tym zdaniu, bo ukrywanie go byłoby drugą
                   nieprawdą. Zgłoszenie z 19.08.2026: przy zmierzonych 8,0 sylaby
                   na sekundę model napisał "bardzo wolno", bo tyle mówiły słowa. */
                <p className="ocena-model__detal">{shot.vocalCuesConflict}</p>
              ) : (
                shot.vocalCues && <p className="ocena-model__detal">{shot.vocalCues}</p>
              )}
              {!shot.emotionError && shot.emotionFunction && (
                <p className="ocena-model__podpowiedz">{shot.emotionFunction}</p>
              )}
            </div>

            {/* Zapis do historii jest wygodą, nie warunkiem powodzenia - ale gdy
                się nie uda, znika porównanie z poprzednią próbą, czyli sedno
                tego trybu. Użytkownik ma prawo wiedzieć, że go nie dostanie. */}
            {shot.persisted === false && (
              <p className="ocena-model__detal">
                Tej próby nie udało się zapisać, więc nie pojawi się w „Ostatnich próbach" i nie będzie
                punktem odniesienia dla następnej. Sam pomiar powyżej jest ważny.
              </p>
            )}

            {/* To zdanie mówiło wcześniej, że model ocenia brzmienie "bez dostępu do
                treści". Transkrypcji faktycznie mu nie podajemy - ale słowa są w samym
                nagraniu i model je słyszy, więc zdanie było nieprawdziwe w skutkach.
                Pomiar z 19.08.2026: na klipach, w których treść przeczy wykonaniu,
                15 z 18 opisów poszło za słowami. docs/eksperyment-przeciek-tresci.md */}
            {/* Paczka audytu przy BIEZACYM wyniku, nie tylko w historii.
                Zgloszenie z 19.08.2026 brzmialo "chce moc pobrac audyt kazdego
                nagrania" - a moment, w ktorym cos wyglada podejrzanie, jest
                dokladnie tym momentem, w ktorym stoi sie na tym ekranie. */}
            {shot.id && (
              <p className="ocena-model__detal">
                <a className="historia-audyt" href={`/api/mic/${shot.id}/audyt.zip`} download>
                  Pobierz audyt tej próby
                </a>{" "}
                - nagranie, transkrypcja, pomiar, pełne zapytanie i surowa odpowiedź modelu.
              </p>
            )}

            {/* Porównanie stoi TU, przy wyniku, a nie w ustawieniach: moment,
                w którym ocena wygląda podejrzanie, jest dokładnie tym momentem,
                w którym chce się ją sprawdzić innym modelem. */}
            {naPorownaj && (
              <p className="ocena-model__detal">
                <Button wariant="secondary" onClick={naPorownaj} zajety={porownanieTrwa}>
                  Porównaj dostawców
                </Button>{" "}
                Puści to samo nagranie przez każdy skonfigurowany model. Płatne.
              </p>
            )}

            <p className="mic-stopka">
              Tempo i ton pochodzą z pomiaru sygnału. Emocja to interpretacja modelu. Transkrypcji mu nie
              podajemy, ale słowa słyszy w samym nagraniu i potrafi opisać to, co ktoś MÓWI, zamiast tego,
              jak to zabrzmiało - dlatego jego opis tempa i tonu zestawiamy w kodzie z pomiarem i odrzucamy,
              gdy się rozjeżdżają.
            </p>
          </>
        )}
      </Card>
    </>
  );
}
